import {Injectable, NgZone} from '@angular/core';
import {SimpleNotificationComponent} from './simple-notification.component';
import {ErrorNotificationComponent} from './error-notification.component';
import {isDefined} from '../../utils/utils';
import {Router} from '@angular/router';
import {MessageService} from 'primeng/api';
import {ToastCloseEvent} from 'primeng/toast';
import {DialogService} from 'primeng/dynamicdialog';
import {take} from 'rxjs/operators';
import {AuthService} from '@webmate-web-core/shared/services/auth.service';
import {DialogComponent} from '@webmate-web-core/shared/components/dialog/dialog.component';
import {IconProp} from '@fortawesome/fontawesome-svg-core';
import {ErrorMessagePipe} from '@webmate-web-core/shared/utils/pipes/error.pipe';
import {GenericApiResponse} from '@webmate-web-core/shared/api/apibase.api';
import {TranslateService} from '@ngx-translate/core';
import {PosthogService} from '@webmate-web-core/shared/services/posthog.service';
import {formatDate} from '@angular/common';
import {PersistenceService} from '@webmate-web-core/shared/services/persistence.service';

interface ScheduledMaintenanceToastData {
    maintenanceId: string,
    status: string,
    updatedAt: string
}

export interface WmDialogConfig {
    msg?: string,
    htmlMsg?: string,
    confirmText: string,
    confirmCallback: Function,
    cancelText?: string,
    cancelCallback?: Function,
    thirdOptionText?: string,
    thirdOptionCallback?: Function
    icon?: IconProp,
}

@Injectable({
    providedIn: 'root'
})
export class WmNotificationService {

    private currentlyOpen: {[s: string]: boolean} = {};
    private errorKey = "#!>ERROR<!#";

    private readonly LOCALSTORAGE_DISMISSED_MAINTENANCE_NOTIFICATIONS_KEY = '_webmate_dismissed_maintenance_notifications';
    private readonly MAINTENANCE_NOTIFICATION_LOOKAHEAD_HOURS = 48;
    private openMaintenanceNotificationKeys = new Set<string>();

    constructor(private router: Router, private zone: NgZone, private toastService: MessageService, private dialogService: DialogService, private authService: AuthService,
                private translateService: TranslateService, private posthog: PosthogService, private persistenceService: PersistenceService){}

    simpleNotification(message: string, btnText?: string, callback?: () => any) {
        if (this.currentlyOpen[message]) {
            return;
        }
        this.currentlyOpen[message] = true;

        let ref = this.dialogService.open(SimpleNotificationComponent, {
            showHeader: false,
            closeOnEscape: false,
            modal: true,
            dismissableMask: true,
            baseZIndex: 10000,
            data: {
                msg: message,
                btnText: btnText
            }
        });
        ref.onClose.pipe(take(1)).subscribe(() => {
            delete this.currentlyOpen[message];
            if (!!callback) {
                callback();
            }
        })
    }

    openWithConfig(config: WmDialogConfig): boolean {
        let primengConfig = {
            showHeader: false,
            closeOnEscape: false,
            modal: true,
            dismissableMask: false,
            baseZIndex: 10000,
            duplicate: true,
            data: config
        };

        let dialogRef = this.dialogService.open(DialogComponent, primengConfig);
        if (!dialogRef) {
            return false;
        }
        dialogRef.onClose.pipe(take(1)).subscribe(result => {
            if (result === 'confirm') {
                config.confirmCallback();
            } else if (result === 'cancel' && config.cancelCallback) {
                config.cancelCallback();
            } else if (result === 'thirdOption' && config.thirdOptionCallback) {
                config.thirdOptionCallback();
            }
        });
        return true;
    }

    open(msg: string, confirmText: string, confirmCallback: Function, cancelText?: string, cancelCallback?: Function,
         thirdOptionText?: string, thirdOptionCallback?: Function) {

        const dialogConfig = {
            msg,
            confirmText,
            confirmCallback,
            cancelText,
            cancelCallback,
            thirdOptionText,
            thirdOptionCallback
        };

        this.openWithConfig(dialogConfig);
    }

    openHtmlMessage(htmlMsg: string, confirmText: string, confirmCallback: Function, cancelText?: string, cancelCallback?: Function,
         thirdOptionText?: string, thirdOptionCallback?: Function) {

        const dialogConfig = {
            htmlMsg,
            confirmText,
            confirmCallback,
            cancelText,
            cancelCallback,
            thirdOptionText,
            thirdOptionCallback
        };

        this.openWithConfig(dialogConfig);
    }

    /**
    * This is a helper to:
    * 1. Have on centralized message
    * 2. not have to inject the router at every place this is used (in the error handling it's problematic)
    */
    goToLoginNotification() {
        this.zone.run(() => {
            this.simpleNotification("Your session has expired. Please re-authenticate to continue.", "Go To Login", () => {
                this.authService.logout();
            });
        })
    }

    private youRegexArr = [
        new RegExp(" you ", 'g'),
        new RegExp("You ", 'g'),
        new RegExp("You,", 'g'),
        new RegExp(" you,", 'g'),
        new RegExp(" you\.", 'g'),
        new RegExp(" you\!", 'g')
    ];

    errorNotification(errorMsg: string, errorId?: string){
        if (!errorMsg){
            return;
        }
        /** This is necessary because the error handler lives outside the regular angular lifecycle*/
        this.zone.run(() => {
            /** in the error-case, we store a message-array and the dialog handle instead of a boolean */
            if (!this.currentlyOpen[this.errorKey]) {

                /** the following is a but weird: Angular's Error-Messages often speak to the programmer directly,
                 * akin to "You did something wrong". We do not want our Users confused, hence we do String-Replacement
                 * and put all the blame on 'webmate' instead. Cannot go wrong with that can we ;-) ?*/

                errorMsg = errorMsg.replace(this.youRegexArr[0], " webmate ");
                errorMsg = errorMsg.replace(this.youRegexArr[1], "webmate ");
                errorMsg = errorMsg.replace(this.youRegexArr[2], "webmate,");
                errorMsg = errorMsg.replace(this.youRegexArr[3], " webmate,");
                errorMsg = errorMsg.replace(this.youRegexArr[4], " webmate.");
                errorMsg = errorMsg.replace(this.youRegexArr[5], " webmate!");

                let dialogConfig = {
                    baseZIndex: 999999,
                    width: '510px',
                    showHeader: false,
                    closeOnEscape: false,
                    dismissableMask: false,
                    data: {
                        errors: [errorMsg],
                        errorId: errorId
                    }
                };

                // posthog error tracking
                this.posthog.capture("$exception", {wmTracking: true, errorId, errorMsg});

                let errorDialog = this.dialogService.open(ErrorNotificationComponent, dialogConfig);

                errorDialog?.onClose.pipe(take(1)).subscribe(result => {
                    delete this.currentlyOpen[this.errorKey];
                });


            }


        });
    }

    toastNotification(msg: string, title?: string, ttl?: number) {
        this.toastService.add({severity:'info', summary: isDefined(title) ? title : 'Info', detail: msg, life: isDefined(ttl) ? ttl : 5000, sticky: false});
    }

    toastDisconnectNotification() {
        this.toastService.add({severity:'warn', key: 'disconnect', life: 30000, sticky: false});
    }

    toastWarningNotification(msg: string, title?: string, ttl?: number) {
        this.toastService.add({severity:'warn', summary: isDefined(title) ? title : 'Warning', detail: msg, life: isDefined(ttl) ? ttl : 5000, sticky: false});
    }

    toastPersistentNotification(msg: string, title?: string) {
        this.toastService.add({severity:'info', summary: isDefined(title) ? title : 'Info', detail: msg, sticky: true});
    }

    toastPersistentActionNotification(msg: string, actionLabel: string, action: () => void, title?: string) {
        this.toastService.clear('workbench-action');
        this.toastService.add({
            key: 'workbench-action',
            severity: 'info',
            summary: isDefined(title) ? title : 'Info',
            detail: msg,
            sticky: true,
            data: {
                actionLabel,
                action
            }
        });
    }

    toastErrorNotification(msg: string) {
        this.toastService.add({severity:'error', summary: 'An error occurred', detail: msg, life: 5000});
    }

    toastErrorCodeNotification(error: string | GenericApiResponse) {
        new ErrorMessagePipe(this.translateService).transform(error).subscribe(message => this.toastErrorNotification(message));
    }

    toastIncidentNotification(msg: string, title: string, status: string) {
        let severity: string;
        switch (status) {
            case 'investigating':
                severity = 'warn';
                break;
            case 'resolved':
                severity = 'success';
                break;
            default:
                severity = 'info';
                break;
        }
        this.toastService.add({severity: severity, key: 'incident', summary: title, detail: msg, sticky: true});
    }

    toastScheduledMaintenanceNotification(maintenanceId: string, msg: string, title: string, status: string, scheduledFor: string, scheduledUntil: string, updatedAt: string) {
        // keyed by the update's own timestamp (not just id+status) so a genuinely new update - e.g. a
        // follow-up message while still "scheduled", or a second "in_progress" progress note - is treated
        // as new and shown again, while re-fetches of the exact same update keep deduplicating correctly
        const key = this.buildScheduledMaintenanceKey(maintenanceId, status, updatedAt);
        if (this.openMaintenanceNotificationKeys.has(key) || this.isScheduledMaintenanceNotificationDismissed(key) || !this.isMaintenanceWithinNotificationWindow(scheduledFor, scheduledUntil)) {
            return;
        }

        let severity: string;
        switch (status) {
            case 'scheduled':
                severity = 'info';
                break;
            case 'in_progress':
                severity = 'warn';
                break;
            case 'completed':
                severity = 'success';
                break;
            default:
                severity = 'info';
                break;
        }

        const startTime = formatDate(scheduledFor, 'MMM d, y HH:mm', 'en-US', 'Europe/Berlin');
        const endTime = formatDate(scheduledUntil, 'MMM d, y HH:mm', 'en-US', 'Europe/Berlin');
        const titleWithDate = `${title} (from ${startTime} to ${endTime})`
        const data: ScheduledMaintenanceToastData = {maintenanceId, status, updatedAt};
        this.openMaintenanceNotificationKeys.add(key);
        this.toastService.add({severity: severity, key: 'scheduled-maintenance', summary: titleWithDate, detail: msg, sticky: true, data});
    }

    onScheduledMaintenanceToastClose(event: ToastCloseEvent) {
        const data = event?.message?.data as ScheduledMaintenanceToastData;
        if (data?.maintenanceId && data?.status && data?.updatedAt) {
            const key = this.buildScheduledMaintenanceKey(data.maintenanceId, data.status, data.updatedAt);
            this.openMaintenanceNotificationKeys.delete(key);
            this.dismissScheduledMaintenanceNotification(key);
        }
    }

    private isMaintenanceWithinNotificationWindow(scheduledFor: string, scheduledUntil: string): boolean {
        // based purely on the maintenance's actual time window, not on which status update is being processed:
        // an "in_progress" update from a maintenance that ended long ago must not bypass the window just
        // because that status string, taken in isolation, looks like it's still ongoing
        const now = Date.now();
        const startTime = Date.parse(scheduledFor);
        const endTime = Date.parse(scheduledUntil);
        const lookaheadMs = this.MAINTENANCE_NOTIFICATION_LOOKAHEAD_HOURS * 60 * 60 * 1000;

        const isCurrentlyOngoing = now >= startTime && now <= endTime;
        const isUpcomingWithinLookahead = now < startTime && startTime <= now + lookaheadMs;
        const hasEndedRecently = now > endTime && endTime >= now - lookaheadMs;
        return isCurrentlyOngoing || isUpcomingWithinLookahead || hasEndedRecently;
    }

    private isScheduledMaintenanceNotificationDismissed(key: string): boolean {
        return this.getDismissedScheduledMaintenanceNotifications().includes(key);
    }

    private dismissScheduledMaintenanceNotification(key: string) {
        const dismissed = this.getDismissedScheduledMaintenanceNotifications();
        if (!dismissed.includes(key)) {
            dismissed.push(key);
            this.persistenceService.setObjectItem(this.LOCALSTORAGE_DISMISSED_MAINTENANCE_NOTIFICATIONS_KEY, dismissed);
        }
    }

    private getDismissedScheduledMaintenanceNotifications(): string[] {
        const stored = this.persistenceService.getObjectItem(this.LOCALSTORAGE_DISMISSED_MAINTENANCE_NOTIFICATIONS_KEY);
        return Array.isArray(stored) ? stored as string[] : [];
    }

    private buildScheduledMaintenanceKey(maintenanceId: string, status: string, updatedAt: string): string {
        return `${maintenanceId}:${status}:${updatedAt}`;
    }
}
